import { useEffect, useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "../components/Button";
import { Card } from "../components/Card";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { FormGroup } from "../components/FormGroup";
import { FormLabel } from "../components/FormLabel";
import { InfoBox } from "../components/InfoBox";
import { LoadingContainer } from "../components/LoadingContainer";
import { LoadingSpinner } from "../components/LoadingSpinner";
import { PageContent } from "../components/PageContent";
import { SectionTitle } from "../components/SectionTitle";
import { SelectInput } from "../components/SelectInput";
import { Toggle } from "../components/Toggle";
import colors from "@styling/colors";
import { spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";
import type { ChainId } from "@KassaroAB/shared/types/ids";
import { JobStatus } from "@KassaroAB/shared/types/enums/JobStatus";
import { isActiveJobStatus } from "@KassaroAB/shared/utils/jobStatus";
import { useGetChainQuery, useUpdateChainMutation } from "../query/chainsQuery";
import {
  useGetChainModeActivationJobsQuery,
  useRetryChainModeActivationJobMutation,
  useStartChainModeActivationJobMutation,
} from "../query/chainModeActivationJobsQuery";
import { useGetChainStoresInfiniteQuery } from "../query/storesQuery";
import { ChainInfoForm } from "./components/ChainInfoForm";
import { ChainCatalogPolicyForm } from "./components/ChainCatalogPolicyForm";
import { ChainCatalogSettingsForm } from "./components/ChainCatalogSettingsForm";

const SettingsColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x3}px;
  max-width: 720px;
`;

const SettingsCard = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x2}px;
`;

const SectionDescription = styled.p`
  margin: 0;
  font-size: ${fontSizes.regular}px;
  color: ${colors.textMuted};
  line-height: 1.5;
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.x2}px;
`;

const ToggleActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.x2}px;
`;

const ProgressText = styled.p`
  margin: 0;
  font-size: ${fontSizes.small}px;
  color: ${colors.textMuted};
`;

export const ChainSettingsView = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { chainId } = useParams<{ chainId: ChainId }>();
  const { data: chain, isLoading } = useGetChainQuery(chainId);
  const { data: jobs } = useGetChainModeActivationJobsQuery(chainId);
  const { data: storesData } = useGetChainStoresInfiniteQuery(chainId);
  const {
    mutate: startActivation,
    isPending: isStarting,
    error: startError,
  } = useStartChainModeActivationJobMutation(chainId);
  const {
    mutate: retryActivation,
    isPending: isRetrying,
    error: retryError,
  } = useRetryChainModeActivationJobMutation(chainId);
  const [sourceStoreId, setSourceStoreId] = useState("");
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const {
    mutate: updateChainInfo,
    isPending: isSavingInfo,
    error: infoError,
  } = useUpdateChainMutation();
  const {
    mutate: updateCatalogPolicy,
    isPending: isSavingPolicy,
    error: policyError,
  } = useUpdateChainMutation();
  const {
    mutate: updateCatalogSettings,
    isPending: isSavingCatalogSettings,
    error: catalogSettingsError,
  } = useUpdateChainMutation();
  const {
    mutate: updateSharedCustomers,
    isPending: isSavingSharedCustomers,
    error: sharedCustomersError,
  } = useUpdateChainMutation();

  const latestJob = jobs?.[0];
  const isJobActive = latestJob ? isActiveJobStatus(latestJob.status) : false;
  const isJobFailed = latestJob?.status === JobStatus.FAILED;
  const isChainModeOn = chain?.chainMode === true;
  const chainModeLocked = isChainModeOn || isJobActive;
  const stores = storesData?.pages.flat() ?? [];
  const activationError = startError || retryError;

  useEffect(() => {
    if (!successMessage) return;
    const timer = setTimeout(() => setSuccessMessage(null), 3000);
    return () => clearTimeout(timer);
  }, [successMessage]);

  useEffect(() => {
    if (latestJob?.status !== JobStatus.COMPLETED) return;
    queryClient.invalidateQueries({ queryKey: ["chains", chainId] });
  }, [latestJob?.status, chainId, queryClient]);

  if (isLoading) {
    return <LoadingContainer message={t("chainSettings.loading")} />;
  }

  if (!chainId || !chain) {
    return <LoadingContainer message={t("chainSettings.notFound")} />;
  }

  return (
    <PageContent title={t("chainSettings.pageTitle")}>
      {successMessage ? (
        <InfoBox variant="success">{successMessage}</InfoBox>
      ) : null}
      <SettingsColumn>
        <SettingsCard>
          <SectionTitle>{t("chainSettings.chainInfoTitle")}</SectionTitle>
          <SectionDescription>
            {t("chainSettings.chainInfoDescription")}
          </SectionDescription>
          <ChainInfoForm
            key={chain.updatedAt}
            chain={chain}
            formKey={chain.updatedAt}
            isLoading={isSavingInfo}
            errorMessage={infoError?.message}
            onSubmit={(value) => {
              updateChainInfo(
                {
                  chainId,
                  chain: {
                    name: value.name.trim(),
                    brand: value.brand.trim(),
                    orgNumber: value.orgNumber.trim(),
                    street: value.street.trim(),
                    postalcode: value.postalcode.trim(),
                    city: value.city.trim(),
                  },
                },
                {
                  onSuccess: () => {
                    setSuccessMessage(t("chainSettings.infoSaved"));
                  },
                },
              );
            }}
          />
        </SettingsCard>

        <SettingsCard>
          <SectionTitle>{t("chainSettings.chainModeTitle")}</SectionTitle>
          <SectionDescription>
            {t("chainSettings.chainModeDescription")}
          </SectionDescription>
          {!isChainModeOn && !isJobActive && !isJobFailed && stores.length > 0 ? (
            <FormGroup>
              <FormLabel>{t("chainSettings.sourceStore")}</FormLabel>
              <SelectInput
                value={sourceStoreId}
                onChange={(e) => setSourceStoreId(e.target.value)}
              >
                <option value="">{t("chainSettings.sourceStoreNone")}</option>
                {stores.map((store) => (
                  <option key={store.id} value={store.id}>
                    {store.displayName || store.name}
                  </option>
                ))}
              </SelectInput>
              <ProgressText>{t("chainSettings.sourceStoreHint")}</ProgressText>
            </FormGroup>
          ) : null}
          <FormGroup>
            <ToggleRow>
              <FormLabel>{t("chainSettings.chainModeLabel")}</FormLabel>
              <ToggleActions>
                {isJobActive || isStarting ? <LoadingSpinner size={16} /> : null}
                <Toggle
                  checked={chainModeLocked}
                  disabled={chainModeLocked || isStarting}
                  onChange={(checked) => {
                    if (!checked || chainModeLocked) return;
                    setIsConfirmOpen(true);
                  }}
                />
              </ToggleActions>
            </ToggleRow>
          </FormGroup>
          {isJobActive && latestJob ? (
            <InfoBox variant="info">
              {t(`chainSettings.phases.${latestJob.phase}`)}
              {latestJob.processed > 0
                ? ` ${t("chainSettings.jobProgress", {
                    copied: latestJob.copied,
                    processed: latestJob.processed,
                  })}`
                : ""}
            </InfoBox>
          ) : null}
          {isChainModeOn && !isJobActive && !isJobFailed ? (
            <InfoBox variant="info">{t("chainSettings.chainModeActive")}</InfoBox>
          ) : null}
          {isChainModeOn ? (
            <>
              <SectionTitle>{t("chainSettings.sharedCustomersTitle")}</SectionTitle>
              <SectionDescription>
                {t("chainSettings.sharedCustomersDescription")}
              </SectionDescription>
              <FormGroup>
                <ToggleRow>
                  <FormLabel>{t("chainSettings.sharedCustomersLabel")}</FormLabel>
                  <ToggleActions>
                    {isSavingSharedCustomers ? <LoadingSpinner size={16} /> : null}
                    <Toggle
                      checked={chain.sharedCustomers === true}
                      disabled={isSavingSharedCustomers}
                      onChange={(checked) => {
                        updateSharedCustomers(
                          {
                            chainId,
                            chain: { sharedCustomers: checked },
                          },
                          {
                            onSuccess: () => {
                              setSuccessMessage(
                                t("chainSettings.sharedCustomersSaved"),
                              );
                            },
                          },
                        );
                      }}
                    />
                  </ToggleActions>
                </ToggleRow>
              </FormGroup>
              {sharedCustomersError ? (
                <InfoBox variant="error">{sharedCustomersError.message}</InfoBox>
              ) : null}
            </>
          ) : null}
          {isJobFailed && latestJob ? (
            <InfoBox variant="error">
              {latestJob.error || t("chainSettings.jobFailed")}
            </InfoBox>
          ) : null}
          {activationError ? (
            <InfoBox variant="error">{activationError.message}</InfoBox>
          ) : null}
          {isJobFailed && latestJob ? (
            <Button
              type="button"
              disabled={isRetrying}
              onClick={() => retryActivation(latestJob.id)}
            >
              {isRetrying
                ? t("chainSettings.retrying")
                : t("chainSettings.retry")}
            </Button>
          ) : null}
        </SettingsCard>

        {isChainModeOn ? (
          <SettingsCard>
            <SectionTitle>{t("chainSettings.catalogSettingsTitle")}</SectionTitle>
            <SectionDescription>
              {t("chainSettings.catalogSettingsDescription")}
            </SectionDescription>
            <ChainCatalogSettingsForm
              key={chain.updatedAt}
              chain={chain}
              formKey={chain.updatedAt}
              isLoading={isSavingCatalogSettings}
              errorMessage={catalogSettingsError?.message}
              onSubmit={(value) => {
                updateCatalogSettings(
                  {
                    chainId,
                    chain: {
                      extraProductFields: value.extraProductFields,
                      defaultVAT: value.defaultVAT,
                    },
                  },
                  {
                    onSuccess: () => {
                      queryClient.invalidateQueries({ queryKey: ["stores"] });
                      setSuccessMessage(t("chainSettings.catalogSettingsSaved"));
                    },
                  },
                );
              }}
            />
          </SettingsCard>
        ) : null}

        {isChainModeOn ? (
          <SettingsCard>
            <SectionTitle>{t("chainSettings.catalogPolicyTitle")}</SectionTitle>
            <SectionDescription>
              {t("chainSettings.catalogPolicyDescription")}
            </SectionDescription>
            <ChainCatalogPolicyForm
              key={chain.updatedAt}
              catalogPolicy={chain.catalogPolicy}
              isLoading={isSavingPolicy}
              errorMessage={policyError?.message}
              onSubmit={(catalogPolicy) => {
                updateCatalogPolicy(
                  {
                    chainId,
                    chain: { catalogPolicy },
                  },
                  {
                    onSuccess: () => {
                      setSuccessMessage(t("chainSettings.policySaved"));
                    },
                  },
                );
              }}
            />
          </SettingsCard>
        ) : null}
      </SettingsColumn>
      <ConfirmDialog
        isOpen={isConfirmOpen}
        title={t("chainSettings.confirmTitle")}
        message={t("chainSettings.confirmMessage")}
        confirmLabel={t("chainSettings.confirmActivate")}
        cancelLabel={t("common.cancel")}
        onCancel={() => setIsConfirmOpen(false)}
        onConfirm={() => {
          setIsConfirmOpen(false);
          startActivation(sourceStoreId || undefined);
        }}
      />
    </PageContent>
  );
};
